import { Alert, Anchor, Breadcrumbs, Card, Grid, Group, Stack, Text } from '@mantine/core';
import { IconHistory, IconLoader } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import CouponForm from '../../components/Coupons/CouponForm';
import PartnerAuthorizationAlert from '../../components/Coupons/PartnerAuthorizationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import { Page } from '../../constants/pageTitles';
import describeDiscount from '../../helpers/describeDiscount';
import useFetch from '../../helpers/useFetch';

const page = `${Page['coupon']}`;

const CouponsDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const domain = useSelector((state) => state.domain.domain);

  const { accountId, domainId, couponId } = useParams();
  const [coupon, setCoupon] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [authorizationUrl, setAuthorizationUrl] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons/${couponId}`)
      .then(({ coupon }) => {
        if (!coupon) {
          navigate(`/accounts/${accountId}/domains/${domainId}/coupons`);
        }
        setCoupon(coupon);
      })
      .catch(setLoadError);
  }, [fetch, navigate, accountId, domainId, couponId]);

  const canGenerateCoupons = Boolean(domain?.capabilities?.generatedCoupons);
  const isGeneratedCoupon = coupon?.source === 'generated';
  const needsPartnerAuthorization =
    canGenerateCoupons && isGeneratedCoupon && coupon.generationErrorCode === 'PARTNER_AUTHORIZATION_REQUIRED';

  useEffect(() => {
    if (!needsPartnerAuthorization) {
      return undefined;
    }
    let cancelled = false;
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons/status`)
      .then((status) => {
        if (!cancelled) {
          setAuthorizationUrl(status?.authorizationUrl || undefined);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAuthorizationUrl(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, needsPartnerAuthorization]);

  const listingPath = `/accounts/${accountId}/domains/${domainId}/coupons${
    canGenerateCoupons ? `?source=${isGeneratedCoupon ? 'generated' : 'upload'}` : ''
  }`;

  const updateGeneratedCoupon = (editedCoupon) =>
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons/${couponId}`, {
      method: 'PUT',
      body: JSON.stringify({
        coupon: { name: editedCoupon.name.trim(), discount: editedCoupon.discount },
      }),
    })
      .then(() => navigate(listingPath))
      .catch(setUserError);

  const update = (coupon, file = null) => {
    if (isGeneratedCoupon) {
      return updateGeneratedCoupon(coupon);
    }
    let formData = new FormData();
    if (file) {
      formData.append('type', file.type);
      formData.append('file', file);
    }
    formData.append('name', coupon.name.trim());
    formData.append('coupon', JSON.stringify(coupon));

    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons/${couponId}`, {
      method: 'PUT',
      headers: {},
      body: formData,
    })
      .then(() => navigate(listingPath))
      .catch(setUserError);
  };

  const del = (cb) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons/${couponId}`, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(listingPath);
      })
      .catch(setLoadError);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!coupon) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#${listingPath}` },
    { title: 'Edit' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${coupon.name} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      {isGeneratedCoupon && (
        <Card withBorder mb="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text fw={600}>{coupon.name}</Text>
                <Text size="sm" c="dimmed">
                  {describeDiscount(coupon.discount, { currency: domain?.currency })}
                </Text>
                <Text size="sm" c="dimmed">
                  Available: {coupon.totalCount ?? 0} / Total: {coupon.codesTotal ?? 0}
                </Text>
              </Stack>
              <Anchor href={`#/accounts/${accountId}/domains/${domainId}/coupons/${couponId}/history`} size="sm">
                <Group gap={4}>
                  <IconHistory size={14} /> View History
                </Group>
              </Anchor>
            </Group>
            {coupon.generationError ? (
              <PartnerAuthorizationAlert
                color="red"
                title="Code generation failed"
                message={coupon.generationError}
                authorizationUrl={needsPartnerAuthorization ? authorizationUrl : undefined}
              />
            ) : (
              coupon.generationPending > 0 && (
                <Alert color="blue" icon={<IconLoader size={16} />}>
                  Generating {coupon.generationPending} codes...
                </Alert>
              )
            )}
          </Stack>
        </Card>
      )}
      <Grid>
        <Grid.Col span={12}>
          <Card withBorder>
            <CouponForm
              coupon={coupon}
              saveCoupon={update}
              deleteCoupon={(cb) => del(cb)}
              source={coupon.source}
              onBack={() => navigate(listingPath)}
            />
          </Card>
        </Grid.Col>
      </Grid>
      {userError && (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      )}
    </>
  );
};

export default CouponsDetailsPage;
